import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
/**
 * Rex Mortis — The Shade's Due
 *
 * Pay the dark a slice of your whole fortune and it will pass you by for a
 * time. Every payment is entered, in public, on the Roll of the Prudent —
 * peace has a price, and a paper trail.
 */
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { formatCrowns } from '../util/formatBig.js';
import type { TributeBoardResponse } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

function holdsFor(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (ms <= 0) return 'moments more';
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  return days > 0 ? `${days}d ${hours}h` : `${hours}h`;
}

export async function renderShadesDue(root: HTMLElement): Promise<void> {
  let board: TributeBoardResponse | null = null;
  let flash: FlashState | null = null;

  async function refresh(): Promise<void> {
    try {
      board = await api.getTributeBoard();
    } catch (err) {
      flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'The altar is silent.' };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    function renderMain(): void {
      const b = board;
      const standing = !b
        ? ''
        : b.wardedUntil
          ? `<span class="pill success">Warded — the Shade passes you by for ${holdsFor(b.wardedUntil)}</span>`
          : b.marked
            ? `<span class="pill danger">🌑 The Shade has your scent — the Due comes too late</span>`
            : `<span class="pill">Unwarded — the dark may take notice</span>`;

      const canPay = !!b && !b.wardedUntil && !b.marked;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/shades-due.webp" alt="" onerror="this.style.display='none'" />
        <h1>The Shade's Due</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          What hunts the mighty can be paid to look away. The price is a twentieth of everything you own, and your name on the Roll.
        </p>

        <div id="flash"></div>

        <section class="panel">
          <h2 style="margin-top:0;">The altar</h2>
          <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); align-items:center;">
            ${standing}
          </div>
          ${
            b
              ? `<dl class="kv" style="margin-top: var(--s-3);">
                   <dt>The Due, priced at your fortune</dt><dd><strong>${formatCrowns(b.costCrowns)} crowns</strong></dd>
                   <dt>What it buys</dt><dd>Seven days the Shade will not mark you</dd>
                 </dl>
                 <div style="margin-top: var(--s-3);">
                   <button class="btn ${canPay ? 'gold' : ''}" id="pay-tribute" ${canPay ? '' : 'disabled'}>
                     Pay the Due
                   </button>
                   ${
                     b.marked
                       ? `<span style="margin-left: var(--s-3); color: var(--c-oxblood); font-style:italic;">Face it, or bear the wound.</span>`
                       : ''
                   }
                 </div>`
              : `<p style="font-style:italic; color:var(--c-ink-soft);">The altar is silent.</p>`
          }
        </section>

        <section class="panel">
          <h2 style="margin-top:0;">The Roll of the Prudent</h2>
          <p style="font-style:italic; color: var(--c-ink-soft);">
            Names the dark spared, for a price. The realm remembers who paid.
          </p>
          ${
            b && b.ledger.length > 0
              ? `<table class="lb-table" style="width:100%;">
                   <tr><th style="text-align:left;">Soul</th><th style="text-align:right;">The Due paid</th><th style="text-align:right;">When</th></tr>
                   ${b.ledger
                     .map(
                       (e) => `
                       <tr>
                         <td>${escapeHtml(e.name)}</td>
                         <td style="text-align:right;">${formatCrowns(e.crowns)} crowns</td>
                         <td style="text-align:right;">${new Date(e.paidAt).toLocaleDateString()}</td>
                       </tr>`,
                     )
                     .join('')}
                 </table>`
              : `<p style="font-style:italic; color:var(--c-ink-soft);">No soul has yet paid. The realm is brave — or poor.</p>`
          }
        </section>
      `;

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      main.querySelector<HTMLButtonElement>('#pay-tribute')?.addEventListener('click', async () => {
        if (!board) return;
        const ok = await showConfirmDialog({
          title: "Pay the Shade's Due?",
          message: `${formatCrowns(board.costCrowns)} crowns go into the dark, and your name goes on the Roll of the Prudent — in public. The Shade will pass you by for seven days.`,
          confirmLabel: 'Pay the Due',
          tone: 'gold',
        });
        if (!ok) return;
        try {
          const r = await api.payTribute();
          store.set({ character: r.character });
          flash = {
            kind: 'success',
            message: `The Due is paid — ${formatCrowns(r.crownsPaid)} crowns into the dark. It will pass you by.`,
          };
          await refresh();
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'The dark refused your coin.' };
        }
        anchored(renderMain);
      });
    }

    anchored(renderMain);
  });
}
